/**
 * The bottom of the person page: Deactivate while any of their accounts is on, Reactivate while
 * any is off (both when the two accounts disagree). Shown only to people holding admin:users:*,
 * and never on your own page.
 */
import { useGetIdentity, useList, usePermissions } from '@refinedev/core';
import { Button, Card, Space, Spin, Typography } from 'antd';
import { hasPermission } from '../../../utils/permissions';
import {
  ACCOUNT_SWITCH_PERMISSION,
  hasSwitchedOffAccount,
  isSwitchedOffEverywhere,
  sameEmail,
  type AccountRow,
} from '../accounts';
import { AccountSwitchButton } from './AccountSwitchButton';

interface AccountStatusFooterProps {
  /** The person's admin app account, from `GET /users/:id`. */
  user: { name?: string | null; email: string; status?: string };
}

const AccountStatusActions: React.FC<AccountStatusFooterProps> = ({ user }) => {
  // `GET /users/:id` does not say about the web app account; the ServeFirst list does, per row.
  // A search can match other emails too, so take only the exact one. Not found (a customer,
  // not on the ServeFirst list) or failed leaves it unknown.
  const lookup = useList<AccountRow>({
    resource: 'users',
    filters: [
      { field: 'group', operator: 'eq', value: 'servefirst' },
      { field: 'search', operator: 'contains', value: user.email },
    ],
    pagination: { current: 1, pageSize: 100 },
    meta: { dataProviderName: 'authDataProvider' },
  });

  if (lookup.isLoading) return <Spin size="small" />;
  // Without a fresh answer we don't know what is on or off, so the buttons wait (a failed check
  // offers Retry): never switch accounts on stale or missing information.
  if (lookup.isError) {
    return (
      <Space>
        <Typography.Text type="secondary">Could not check their web app account.</Typography.Text>
        <Button size="small" onClick={() => lookup.refetch()}>
          Retry
        </Button>
      </Space>
    );
  }
  const row = lookup.data?.data.find((r) => sameEmail(r.email, user.email));
  // Not found: not on the ServeFirst list (a customer, say), so the web app account is unknown.
  const person: AccountRow = { ...user, platformAccount: row?.platformAccount };
  // Paused: a retry waits while the tab is in the background, the old answer still shown.
  const checking = lookup.isFetching || lookup.isPaused;

  return (
    <Space>
      {!isSwitchedOffEverywhere(person) && (
        <AccountSwitchButton action="deactivate" user={person} disabled={checking} />
      )}
      {hasSwitchedOffAccount(person) && (
        <AccountSwitchButton action="reactivate" user={person} disabled={checking} />
      )}
    </Space>
  );
};

export const AccountStatusFooter: React.FC<AccountStatusFooterProps> = ({ user }) => {
  const { data: permissions } = usePermissions<string[]>();
  const { data: identity } = useGetIdentity<{ email?: string }>();
  if (!hasPermission(permissions ?? [], ACCOUNT_SWITCH_PERMISSION)) return null;
  // Until we know who is signed in, we can't tell whether this is their own page.
  if (!identity?.email || !user.email || sameEmail(identity.email, user.email)) return null;
  return (
    <Card title="Account status" style={{ marginTop: 16 }}>
      <AccountStatusActions user={user} />
    </Card>
  );
};
